import { createRouter, createWebHistory } from 'vue-router'
import { api } from './api'
import { i18n } from './i18n'

// Lazy load all views for better code splitting
const Home = () => import('./views/Home.vue')
const Page = () => import('./views/Page.vue')
const Discography = () => import('./views/Discography.vue')
const Contact = () => import('./views/Contact.vue')
const NotFound = () => import('./views/NotFound.vue')
const AdminLogin = () => import('./views/AdminLogin.vue')
const AdminPages = () => import('./views/AdminPages.vue')
const AdminEditPage = () => import('./views/AdminEditPage.vue')
const AdminMembers = () => import('./views/AdminMembers.vue')
const AdminEditMember = () => import('./views/AdminEditMember.vue')
const AdminUsers = () => import('./views/AdminUsers.vue')

const routes = [
  { path: '/', component: Home, meta: { titleKey: 'nav.home' } },
  { path: '/albums', component: Discography, meta: { titleKey: 'nav.albums' } },
  { path: '/songs', redirect: '/albums' }, // Redirect old URL to new one
  { path: '/album/:slug', component: () => import('./views/Album.vue'), meta: { titleKey: 'nav.albums' } },
  { path: '/appearances', alias: ['/page/appearances'], component: () => import('./views/Appearances.vue'), meta: { titleKey: 'appearances.title' } },
  { path: '/appearance/:id', component: () => import('./views/Appearance.vue'), meta: { titleKey: 'appearances.title' } },
  { path: '/gallery', alias: ['/page/gallery'], component: () => import('./views/Gallery.vue'), meta: { titleKey: 'nav.gallery' } },
  // Member individual pages kept for SEO; list embedded in about page
  { path: '/member/:slug', component: () => import('./views/Member.vue'), meta: { titleKey: 'nav.members' } },
  { path: '/contact', component: Contact, meta: { titleKey: 'nav.contact' } },
  { path: '/page/:slug', component: Page, meta: { titleKey: 'nav.home' } },
  { path: '/admin/login', component: AdminLogin, meta: { titleKey: 'admin.loginTitle' } },
  { path: '/admin/pages', component: AdminPages, meta: { titleKey: 'admin.pages', requiresAuth: true } },
  { path: '/admin/landing', component: () => import('./views/AdminLanding.vue'), meta: { titleKey: 'landing.manage', requiresAuth: true } },
  { path: '/admin/messages', component: () => import('./views/AdminMessages.vue'), meta: { titleKey: 'admin.messages', requiresAuth: true } },
  { path: '/admin/pages/:id', component: AdminEditPage, meta: { titleKey: 'admin.pageEdit', requiresAuth: true } },
  { path: '/admin/members', component: AdminMembers, meta: { titleKey: 'admin.pages', requiresAuth: true } },
  { path: '/admin/members/:id', component: AdminEditMember, meta: { titleKey: 'admin.pages', requiresAuth: true } },
  { path: '/admin/albums', component: () => import('./views/AdminAlbums.vue'), meta: { titleKey: 'nav.albums', requiresAuth: true } },
  { path: '/admin/albums/:id', component: () => import('./views/AdminEditAlbum.vue'), meta: { titleKey: 'nav.albums', requiresAuth: true } },
  { path: '/admin/settings', component: () => import('./views/AdminSettings.vue'), meta: { titleKey: 'admin.settings', requiresAuth: true } },
  { path: '/admin/users', component: AdminUsers, meta: { titleKey: 'admin.users', requiresAuth: true } },
  { path: '/admin/appearances', component: () => import('./views/AdminAppearances.vue'), meta: { titleKey: 'appearances.title', requiresAuth: true } },
  { path: '/admin/appearances/:id', component: () => import('./views/AdminEditAppearance.vue'), meta: { titleKey: 'appearances.title', requiresAuth: true } },
  { path: '/admin/gallery', component: () => import('./views/AdminGallery.vue'), meta: { titleKey: 'admin.gallery', requiresAuth: true } },
  { path: '/admin/gallery/:id', component: () => import('./views/AdminEditGallery.vue'), meta: { titleKey: 'admin.gallery', requiresAuth: true } },
  { path: '/admin/analytics', component: () => import('./views/AdminAnalytics.vue'), meta: { titleKey: 'analytics.title', requiresAuth: true } },
  { path: '/:pathMatch(.*)*', component: NotFound, meta: { titleKey: 'common.notFound' } }
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() { return { top: 0 } }
})

function upsertMeta(name, content, attr = 'name') {
  if (!content) return
  let el = document.head.querySelector(`meta[${attr}="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel, href, hreflang = null) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let el = document.head.querySelector(selector)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    if (hreflang) el.setAttribute('hreflang', hreflang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

// With httpOnly cookies, we can't check auth status client-side
// Let the backend validate; if unauthorized, redirect to home to hide admin routes
router.beforeEach(async (to) => {
  if (to.meta.requiresAuth) {
    // Validate by testing protected endpoint
    try {
      await api.get('/auth/me')
    } catch (err) {
      if (err?.response?.status === 401) {
        // Redirect to home instead of login to hide admin routes from regular users
        return '/'
      }
    }
  }
})

router.afterEach((to) => {
  const base = 'Chorostalites'
  const key = to.meta.titleKey
  const translated = key ? i18n.global.t(key) : ''
  document.title = translated ? `${translated} | ${base}` : base

  const locale = i18n.global.locale.value || 'el'
  const path = to.fullPath || to.path || '/'
  const canonical = `${window.location.origin}${path}`
  const englishPath = canonical
  const greekPath = canonical

  const defaultDescEl = 'Χοροσταλίτες - Παραδοσιακό μουσικό σχήμα.'
  const defaultDescEn = 'Chorostalites - Traditional music band.'
  const routeDescriptions = {
    '/albums': {
      el: 'Δισκογραφία και τραγούδια των Χοροσταλιτών.',
      en: 'Discography and songs by Chorostalites.'
    },
    '/appearances': {
      el: 'Επόμενες και παλαιότερες εμφανίσεις των Χοροσταλιτών.',
      en: 'Upcoming and past live appearances by Chorostalites.'
    },
    '/gallery': {
      el: 'Φωτογραφίες και στιγμές από την πορεία των Χοροσταλιτών.',
      en: 'Photos and moments from Chorostalites.'
    },
    '/contact': {
      el: 'Επικοινωνήστε με τους Χοροσταλίτες.',
      en: 'Contact Chorostalites.'
    }
  }
  const routeKey = Object.keys(routeDescriptions).find((p) => path.startsWith(p))
  const description = routeKey
    ? (locale.startsWith('en') ? routeDescriptions[routeKey].en : routeDescriptions[routeKey].el)
    : (locale.startsWith('en') ? defaultDescEn : defaultDescEl)

  upsertMeta('description', description)
  upsertMeta('robots', to.path.startsWith('/admin') ? 'noindex,nofollow' : 'index,follow')
  upsertLink('canonical', canonical)
  upsertLink('alternate', greekPath, 'el')
  upsertLink('alternate', englishPath, 'en')
  
  // Track page view (async, non-blocking)
  if (!to.meta.requiresAuth && !to.path.startsWith('/admin')) {
    try {
      api.post('/analytics/track').catch(() => {
        // Silently fail - don't break navigation
      })
    } catch (e) {
      // Ignore tracking errors
    }
  }
})

export default router
